/**
 * Annotation Rendering Service
 *
 * Renders annotations (bounding boxes, segmentation masks, measurement labels)
 * onto images using HTML5 Canvas. Visual styling matches the iOS app.
 *
 * iOS source reference: Glean/CapturedImageView.swift
 */

import type {
  CaptureMetadata,
  Annotation,
  BBoxPixels,
  PhysicalMeasurements,
  SegmentationData,
  CGPointCodable,
  KeypointAnnotation,
} from "@/types/annotations";
import { decodeCompressedRLEToMask } from "@/utils/rleUtils";
import {
  buildAnnotationMeasurementLabels,
  buildMeasurementLineDrawSpecs,
  type MeasurementLineDrawSpec,
} from "@/utils/annotationMeasurementUtils";
import {
  buildAnnotationClassRegistryForAnnotations,
  getAnnotationClassColor,
  resolveAnnotationClassName,
  resolveAnnotationRenderColor,
} from "@shared/annotationClassColors";

export interface RenderOptions {
  showLength: boolean;
  showWidth: boolean;
  showMass: boolean;
  showKeypoints?: boolean;
  showSkeleton?: boolean;
  showMeasureBoundary?: boolean;
  showKeypointLabels?: boolean;
  showText?: boolean; // Toggle for measurement text labels (default true)
  jpegQuality?: number; // 0-1, default 0.92
  /** Task-level exact class names. More than one enables class-aware colors. */
  annotationClassNames?: string[];
  /** Task-wide prompt order used only to keep shared class colors stable across traits. */
  annotationClassColorOrder?: string[];
}

export function shouldDrawMeasurementAxes(annotation: Pick<Annotation, "keypoints" | "measurements">): boolean {
  return buildMeasurementLineDrawSpecs(annotation.keypoints).length > 0;
}

export function shouldDrawKeypointSkeleton(
  annotation: Pick<Annotation, "keypoints" | "measurements" | "category">
): boolean {
  const { keypoints, category } = annotation;
  if (!keypoints?.length || !category?.keypointSkeleton?.length) return false;
  if (buildMeasurementLineDrawSpecs(keypoints).length > 0) return false;
  return true;
}

const AXIS_LINE_WIDTH = 6;
const AXIS_ENDPOINT_RADIUS = 10;

const MASK_ALPHA = 0.4;

const LABEL_FONT_SIZE = 28;
const LABEL_PADDING = 4;
const LABEL_BACKGROUND = "rgba(0, 0, 0, 0.7)";
const LABEL_TEXT_COLOR = "#FFFFFF";

const KEYPOINT_RADIUS = 20;
const KEYPOINT_STROKE_WIDTH = 6;
const KEYPOINT_FILL_ALPHA = 0.9;
const KEYPOINT_LABEL_FONT_SIZE = 28;
const KEYPOINT_LABEL_OFFSET_Y = -30;

const KEYPOINT_USER_COLOR = "#FFA500";
const KEYPOINT_MODEL_COLOR = "#00FF00";

const KEYPOINT_VISIBILITY_COLORS: Record<number, string> = {
  0: "#808080",
  1: "#FFA500",
  2: "#00FF00",
};

const SKELETON_LINE_WIDTH = 8;
const SKELETON_LINE_ALPHA = 0.9;

const MEASURE_BOUNDARY_LINE_WIDTH = 8;
const MEASURE_BOUNDARY_COLOR = "#FFFF00";
const BBOX_LINE_WIDTH = 4;

function isManualBBoxAnnotation(annotation: Annotation): boolean {
  return Boolean(
    annotation.bbox && annotation.userAdjustment?.manuallyCreated && "humanAnnotated" in annotation.source
  );
}

export function getAnnotationRenderColor(
  annotation: Annotation,
  index: number,
  annotationClassNames?: readonly string[],
  annotationClassColorOrder?: readonly string[]
): string {
  return resolveAnnotationRenderColor({
    categoryName: annotation.category?.name ?? "object",
    classLabel:
      typeof annotation.attributes?.class_label === "string" ? annotation.attributes.class_label : undefined,
    hasCategoryChange: Boolean(annotation.userAdjustment?.categoryChange),
    annotationIndex: index,
    isManual: isManualBBoxAnnotation(annotation),
    classNames: annotationClassNames,
    classColorOrder: annotationClassColorOrder,
  });
}

/**
 * Render annotations onto an image and return as JPEG blob.
 *
 * @param imageUrl - URL to the original image (Firebase Storage URL)
 * @param metadata - CaptureMetadata containing annotations
 * @param options - Rendering options (which measurements to show)
 * @returns Promise<Blob> - JPEG image with annotations rendered
 */
export async function renderAnnotatedImage(
  imageUrl: string,
  metadata: CaptureMetadata,
  options: RenderOptions
): Promise<Blob> {
  // Load the image with explicit EXIF orientation handling
  // Using createImageBitmap ensures consistent orientation across browsers
  const imageBitmap = await loadImageBitmapWithOrientation(imageUrl);

  // The loaded image dimensions are in display orientation (EXIF applied)
  const displayWidth = imageBitmap.width;
  const displayHeight = imageBitmap.height;

  // Use metadata dimensions directly - annotations are stored in these coordinates
  const rawWidth = metadata.image.width;
  const rawHeight = metadata.image.height;

  // Create canvas at display dimensions (matching what browser shows)
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Failed to get canvas 2D context");
  }

  canvas.width = displayWidth;
  canvas.height = displayHeight;

  // Draw image as-is (EXIF orientation already applied by createImageBitmap)
  ctx.drawImage(imageBitmap, 0, 0);

  // Get measure boundary if present
  const measureBoundary = metadata.annotations?.measureBoundaryPixels;

  // Draw measure boundary first (background layer)
  // Use "up" orientation since image is already display-oriented
  if (options.showMeasureBoundary && measureBoundary) {
    drawMeasureBoundary(ctx, measureBoundary, rawWidth, rawHeight, "up");
  }

  // Get visible annotations
  const allAnnotations = metadata.annotations?.annotations ?? [];
  const visibleAnnotations = allAnnotations.filter((annotation) => annotation.userVisibility.isVisible);
  const classRegistry = buildAnnotationClassRegistryForAnnotations(
    options.annotationClassNames,
    allAnnotations,
    options.annotationClassColorOrder
  );

  // Draw each annotation
  // Use "up" orientation since image is already display-oriented via createImageBitmap
  visibleAnnotations.forEach((annotation, index) => {
    const resolvedClassName = resolveAnnotationClassName(annotation, options.annotationClassNames);
    const color = classRegistry.isMultiClass
      ? getAnnotationClassColor(classRegistry, resolvedClassName)
      : getAnnotationRenderColor(annotation, index, options.annotationClassNames, options.annotationClassColorOrder);
    drawAnnotation(
      ctx,
      annotation,
      color,
      options,
      rawWidth,
      rawHeight,
      "up",
      displayWidth,
      displayHeight,
      measureBoundary
    );
  });

  // Export as JPEG
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create image blob"));
        }
      },
      "image/jpeg",
      options.jpegQuality ?? 0.92
    );
  });
}

// ============================================================================
// IMAGE LOADING
// ============================================================================

/**
 * Load an image as ImageBitmap with EXIF orientation applied.
 * This ensures the image is oriented correctly to match what <img> tags display.
 */
async function loadImageBitmapWithOrientation(url: string): Promise<ImageBitmap> {
  const response = await fetch(url, { mode: "cors" });
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
  }
  const blob = await response.blob();

  // Use createImageBitmap with imageOrientation: 'from-image' to apply EXIF orientation
  // This matches how <img> tags display the image
  return createImageBitmap(blob, {
    imageOrientation: "from-image",
  });
}

// ============================================================================
// COORDINATE TRANSFORMATION
// ============================================================================

/**
 * Transform bbox coordinates from raw image space to displayed space.
 * Annotations are stored in raw image coordinates and need transformation
 * when the image orientation requires rotation.
 */
function transformBBoxForOrientation(
  bbox: BBoxPixels,
  rawWidth: number,
  rawHeight: number,
  orientation: string
): BBoxPixels {
  switch (orientation) {
    case "right":
      // 90° clockwise rotation
      // Point (x,y) -> (rawHeight - y, x)
      return {
        x: rawHeight - bbox.y - bbox.height,
        y: bbox.x,
        width: bbox.height,
        height: bbox.width,
      };
    case "down":
      // 180° rotation
      return {
        x: rawWidth - bbox.x - bbox.width,
        y: rawHeight - bbox.y - bbox.height,
        width: bbox.width,
        height: bbox.height,
      };
    case "left":
      // 90° counter-clockwise rotation
      // Point (x,y) -> (y, rawWidth - x)
      return {
        x: bbox.y,
        y: rawWidth - bbox.x - bbox.width,
        width: bbox.height,
        height: bbox.width,
      };
    case "up":
    default:
      return bbox;
  }
}

/**
 * Transform a single point from raw image space to displayed space.
 * Used for keypoints and boundary polygon vertices.
 */
function transformPointForOrientation(
  x: number,
  y: number,
  rawWidth: number,
  rawHeight: number,
  orientation: string
): { x: number; y: number } {
  switch (orientation) {
    case "right":
      // 90° clockwise rotation: (x,y) -> (rawHeight - y, x)
      return { x: rawHeight - y, y: x };
    case "down":
      // 180° rotation: (x,y) -> (rawWidth - x, rawHeight - y)
      return { x: rawWidth - x, y: rawHeight - y };
    case "left":
      // 90° counter-clockwise rotation: (x,y) -> (y, rawWidth - x)
      return { x: y, y: rawWidth - x };
    case "up":
    default:
      return { x, y };
  }
}

/**
 * Rotate a 2D boolean mask by 90° clockwise.
 */
function rotateMask90CW(mask: boolean[][]): boolean[][] {
  const oldHeight = mask.length;
  const oldWidth = mask[0]?.length ?? 0;
  const newHeight = oldWidth;
  const newWidth = oldHeight;

  const rotated: boolean[][] = Array.from({ length: newHeight }, () => new Array(newWidth).fill(false));

  for (let y = 0; y < oldHeight; y++) {
    for (let x = 0; x < oldWidth; x++) {
      rotated[x][oldHeight - 1 - y] = mask[y][x];
    }
  }

  return rotated;
}

/**
 * Rotate a 2D boolean mask by 90° counter-clockwise.
 */
function rotateMask90CCW(mask: boolean[][]): boolean[][] {
  const oldHeight = mask.length;
  const oldWidth = mask[0]?.length ?? 0;
  const newHeight = oldWidth;
  const newWidth = oldHeight;

  const rotated: boolean[][] = Array.from({ length: newHeight }, () => new Array(newWidth).fill(false));

  for (let y = 0; y < oldHeight; y++) {
    for (let x = 0; x < oldWidth; x++) {
      rotated[oldWidth - 1 - x][y] = mask[y][x];
    }
  }

  return rotated;
}

/**
 * Rotate a 2D boolean mask by 180°.
 */
function rotateMask180(mask: boolean[][]): boolean[][] {
  const height = mask.length;
  const width = mask[0]?.length ?? 0;

  const rotated: boolean[][] = Array.from({ length: height }, () => new Array(width).fill(false));

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      rotated[height - 1 - y][width - 1 - x] = mask[y][x];
    }
  }

  return rotated;
}

/**
 * Transform mask to match image orientation.
 */
function transformMaskForOrientation(mask: boolean[][], orientation: string): boolean[][] {
  switch (orientation) {
    case "right":
      return rotateMask90CW(mask);
    case "down":
      return rotateMask180(mask);
    case "left":
      return rotateMask90CCW(mask);
    case "up":
    default:
      return mask;
  }
}

// ============================================================================
// ANNOTATION DRAWING
// ============================================================================

function drawBoundingBox(ctx: CanvasRenderingContext2D, bbox: BBoxPixels, color: string): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = BBOX_LINE_WIDTH;
  ctx.strokeRect(bbox.x, bbox.y, bbox.width, bbox.height);
  ctx.restore();
}

/**
 * Draw a single annotation (bbox, mask, keypoints, skeleton, labels).
 * Handles both bbox-based annotations and image-wide annotations.
 */
function drawAnnotation(
  ctx: CanvasRenderingContext2D,
  annotation: Annotation,
  color: string,
  options: RenderOptions,
  rawWidth: number,
  rawHeight: number,
  orientation: string,
  displayWidth: number,
  displayHeight: number,
  measureBoundary?: CGPointCodable[]
): void {
  const { bbox, segmentation, measurements, keypoints, category } = annotation;

  // PATH 1: Annotations with bounding box (object detection)
  if (bbox) {
    // Transform bbox from raw to displayed coordinates
    const displayBbox = transformBBoxForOrientation(bbox, rawWidth, rawHeight, orientation);

    // Draw segmentation mask
    if (segmentation) {
      drawSegmentationMask(ctx, displayBbox, segmentation, color, orientation);
    } else {
      drawBoundingBox(ctx, displayBbox, color);
    }
    // Check if this annotation has physical measurement keypoints (length/width axes).
    // Keypoint-only stand count and LAI annotations can also use named points, but
    // they should render as points/masks rather than image-wide measurement axes.
    const hasMeasurementKeypoints = shouldDrawMeasurementAxes(annotation);

    // Draw measurement axes (length in blue, width in red) - iOS style
    if (hasMeasurementKeypoints && keypoints) {
      drawMeasurementAxes(ctx, keypoints, rawWidth, rawHeight, orientation, options);
    } else {
      // Draw skeleton lines (before keypoints so dots appear on top)
      if (options.showSkeleton && shouldDrawKeypointSkeleton(annotation)) {
        drawSkeleton(ctx, keypoints, category.keypointSkeleton, color, rawWidth, rawHeight, orientation);
      }

      // Draw keypoints
      if (options.showKeypoints && keypoints) {
        drawKeypoints(
          ctx,
          keypoints,
          color,
          rawWidth,
          rawHeight,
          orientation,
          options.showKeypointLabels,
          annotation.source
        );
      }
    }

    // Draw measurement labels (old style - black background, below bbox)
    if (measurements) {
      drawMeasurementLabels(ctx, displayBbox, measurements, options);
    }

    return;
  }

  // PATH 2: Annotations without bounding box (image-wide)
  // For LAI: full-image segmentation mask
  if (segmentation) {
    drawImageWideSegmentation(
      ctx,
      segmentation,
      color,
      displayWidth,
      displayHeight,
      orientation,
      measureBoundary,
      rawWidth,
      rawHeight
    );
  }

  // For spinach stand count: image-wide keypoints (no bbox)
  if (options.showKeypoints && keypoints) {
    drawKeypoints(
      ctx,
      keypoints,
      color,
      rawWidth,
      rawHeight,
      orientation,
      options.showKeypointLabels,
      annotation.source
    );
  }
}

/**
 * Draw segmentation mask as colored overlay.
 */
function drawSegmentationMask(
  ctx: CanvasRenderingContext2D,
  bbox: BBoxPixels,
  segmentation: SegmentationData,
  color: string,
  orientation: string = "up"
): void {
  let mask = decodeCompressedRLEToMask(segmentation.compressedRLE);
  if (!mask) {
    console.warn("Failed to decode segmentation mask");
    return;
  }

  // Transform mask to match image orientation
  if (orientation !== "up") {
    mask = transformMaskForOrientation(mask, orientation);
  }

  // The mask is at a downsampled resolution - scale it to bbox dimensions
  const maskHeight = mask.length;
  const maskWidth = mask[0]?.length ?? 0;

  if (maskHeight === 0 || maskWidth === 0) {
    return;
  }

  // Calculate scale factors
  const scaleX = bbox.width / maskWidth;
  const scaleY = bbox.height / maskHeight;

  // Parse color to RGB for alpha compositing
  const rgb = hexToRgb(color);
  if (!rgb) {
    return;
  }

  // Draw mask pixels
  ctx.save();
  ctx.globalAlpha = MASK_ALPHA;

  // Create an offscreen canvas for the mask
  const maskCanvas = document.createElement("canvas");
  maskCanvas.width = Math.ceil(bbox.width);
  maskCanvas.height = Math.ceil(bbox.height);
  const maskCtx = maskCanvas.getContext("2d");
  if (!maskCtx) {
    ctx.restore();
    return;
  }

  // Fill mask canvas with color where mask is true
  const imageData = maskCtx.createImageData(maskCanvas.width, maskCanvas.height);
  const data = imageData.data;

  for (let y = 0; y < maskCanvas.height; y++) {
    for (let x = 0; x < maskCanvas.width; x++) {
      // Map canvas pixel back to mask pixel
      const maskX = Math.floor(x / scaleX);
      const maskY = Math.floor(y / scaleY);

      // Bounds check
      if (maskY < maskHeight && maskX < maskWidth && mask[maskY][maskX]) {
        const idx = (y * maskCanvas.width + x) * 4;
        data[idx] = rgb.r;
        data[idx + 1] = rgb.g;
        data[idx + 2] = rgb.b;
        data[idx + 3] = 255; // Full opacity (globalAlpha handles transparency)
      }
    }
  }

  maskCtx.putImageData(imageData, 0, 0);

  // Draw mask canvas onto main canvas at bbox position
  ctx.drawImage(maskCanvas, bbox.x, bbox.y);
  ctx.restore();
}

/**
 * Draw measurement labels near the bounding box.
 * Respects options.showText - if false, labels are not drawn.
 */
function drawMeasurementLabels(
  ctx: CanvasRenderingContext2D,
  bbox: BBoxPixels,
  measurements: PhysicalMeasurements,
  options: RenderOptions
): void {
  // Skip if text labels are disabled
  if (options.showText === false) {
    return;
  }

  const labels = buildAnnotationMeasurementLabels(measurements, options);

  if (labels.length === 0) {
    return;
  }

  const labelText = labels.join(", ");

  // Position label at bottom of bbox
  const labelX = bbox.x;
  const labelY = bbox.y + bbox.height + LABEL_FONT_SIZE + LABEL_PADDING;

  // Set up text styling
  ctx.font = `${LABEL_FONT_SIZE}px sans-serif`;
  const textMetrics = ctx.measureText(labelText);
  const textWidth = textMetrics.width;
  const textHeight = LABEL_FONT_SIZE;

  // Draw background
  ctx.fillStyle = LABEL_BACKGROUND;
  ctx.fillRect(
    labelX - LABEL_PADDING,
    labelY - textHeight - LABEL_PADDING,
    textWidth + LABEL_PADDING * 2,
    textHeight + LABEL_PADDING * 2
  );

  // Draw text
  ctx.fillStyle = LABEL_TEXT_COLOR;
  ctx.fillText(labelText, labelX, labelY);
}

/**
 * Draw measurement axes (length in blue, width in red) matching iOS behavior.
 * Finds keypoints by name and draws colored lines with endpoint circles.
 * Text labels are handled separately by drawMeasurementLabels for the old style.
 */
function drawMeasurementAxes(
  ctx: CanvasRenderingContext2D,
  keypoints: KeypointAnnotation[],
  rawWidth: number,
  rawHeight: number,
  orientation: string,
  options: RenderOptions
): void {
  const shouldDraw = (spec: MeasurementLineDrawSpec): boolean => {
    if (spec.key === "length") return options.showLength;
    if (spec.key === "width") return options.showWidth;
    return true;
  };

  for (const spec of buildMeasurementLineDrawSpecs(keypoints).filter(shouldDraw)) {
    const p1 = transformPointForOrientation(spec.start.x, spec.start.y, rawWidth, rawHeight, orientation);
    const p2 = transformPointForOrientation(spec.end.x, spec.end.y, rawWidth, rawHeight, orientation);

    ctx.save();
    ctx.strokeStyle = spec.color;
    ctx.lineWidth = AXIS_LINE_WIDTH;
    ctx.lineCap = "round";

    // Draw line
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();

    // Draw endpoint circles
    ctx.fillStyle = spec.color;
    ctx.beginPath();
    ctx.arc(p1.x, p1.y, AXIS_ENDPOINT_RADIUS, 0, 2 * Math.PI);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(p2.x, p2.y, AXIS_ENDPOINT_RADIUS, 0, 2 * Math.PI);
    ctx.fill();

    ctx.restore();
  }
}

/**
 * Draw keypoints as colored circles.
 */
function drawKeypoints(
  ctx: CanvasRenderingContext2D,
  keypoints: KeypointAnnotation[],
  color: string,
  rawWidth: number,
  rawHeight: number,
  orientation: string,
  showLabels: boolean = false,
  source?: Record<string, unknown>
): void {
  // Determine if this is a user-created keypoint
  const isUserKeypoint = source && "humanAnnotated" in source;
  const sourceColor = isUserKeypoint ? KEYPOINT_USER_COLOR : KEYPOINT_MODEL_COLOR;

  for (const kp of keypoints) {
    // Skip unlabeled keypoints (visibility 0)
    if (kp.visibility === 0) continue;

    // Transform point for orientation
    const transformed = transformPointForOrientation(kp.x, kp.y, rawWidth, rawHeight, orientation);

    // Use source-based color for keypoints (matches KeyPoints component)
    const fillColor = sourceColor;

    // Draw filled circle with white stroke
    ctx.save();
    ctx.globalAlpha = KEYPOINT_FILL_ALPHA;
    ctx.fillStyle = fillColor;
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = KEYPOINT_STROKE_WIDTH;

    ctx.beginPath();
    ctx.arc(transformed.x, transformed.y, KEYPOINT_RADIUS, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    // Draw label if enabled and name exists
    if (showLabels && kp.name) {
      ctx.save();
      ctx.fillStyle = "#FFFFFF";
      ctx.font = `${KEYPOINT_LABEL_FONT_SIZE}px sans-serif`;
      ctx.textAlign = "center";
      ctx.shadowColor = "rgba(0, 0, 0, 0.7)";
      ctx.shadowBlur = 2;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;
      ctx.fillText(kp.name, transformed.x, transformed.y + KEYPOINT_LABEL_OFFSET_Y);
      ctx.restore();
    }
  }
}

/**
 * Draw skeleton lines connecting keypoint pairs.
 */
function drawSkeleton(
  ctx: CanvasRenderingContext2D,
  keypoints: KeypointAnnotation[],
  skeleton: number[][],
  color: string,
  rawWidth: number,
  rawHeight: number,
  orientation: string
): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = SKELETON_LINE_WIDTH;
  ctx.globalAlpha = SKELETON_LINE_ALPHA;
  ctx.lineCap = "round";

  for (const [idx1, idx2] of skeleton) {
    const kp1 = keypoints[idx1];
    const kp2 = keypoints[idx2];

    // Skip if either keypoint is missing or unlabeled
    if (!kp1 || !kp2) continue;
    if (kp1.visibility === 0 || kp2.visibility === 0) continue;

    // Transform both points
    const p1 = transformPointForOrientation(kp1.x, kp1.y, rawWidth, rawHeight, orientation);
    const p2 = transformPointForOrientation(kp2.x, kp2.y, rawWidth, rawHeight, orientation);

    // Draw line
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * Draw the AR measure boundary polygon.
 */
function drawMeasureBoundary(
  ctx: CanvasRenderingContext2D,
  boundaryPoints: CGPointCodable[],
  rawWidth: number,
  rawHeight: number,
  orientation: string
): void {
  if (!boundaryPoints || boundaryPoints.length < 3) return;

  // Transform all boundary points
  const transformedPoints = boundaryPoints.map((p) =>
    transformPointForOrientation(p.x, p.y, rawWidth, rawHeight, orientation)
  );

  ctx.save();
  ctx.strokeStyle = MEASURE_BOUNDARY_COLOR;
  ctx.lineWidth = MEASURE_BOUNDARY_LINE_WIDTH;

  ctx.beginPath();
  ctx.moveTo(transformedPoints[0].x, transformedPoints[0].y);
  for (let i = 1; i < transformedPoints.length; i++) {
    ctx.lineTo(transformedPoints[i].x, transformedPoints[i].y);
  }
  ctx.closePath();
  ctx.stroke();

  ctx.restore();
}

/**
 * Draw image-wide segmentation mask (for LAI).
 * Optionally clips to the measure boundary polygon.
 */
function drawImageWideSegmentation(
  ctx: CanvasRenderingContext2D,
  segmentation: SegmentationData,
  color: string,
  displayWidth: number,
  displayHeight: number,
  orientation: string,
  clipBoundary?: CGPointCodable[],
  rawWidth?: number,
  rawHeight?: number
): void {
  let mask = decodeCompressedRLEToMask(segmentation.compressedRLE);
  if (!mask) {
    console.warn("Failed to decode image-wide segmentation mask");
    return;
  }

  // Transform mask for orientation
  if (orientation !== "up") {
    mask = transformMaskForOrientation(mask, orientation);
  }

  const maskHeight = mask.length;
  const maskWidth = mask[0]?.length ?? 0;

  if (maskHeight === 0 || maskWidth === 0) {
    return;
  }

  // Calculate scale to display dimensions
  const scaleX = displayWidth / maskWidth;
  const scaleY = displayHeight / maskHeight;

  const rgb = hexToRgb(color);
  if (!rgb) return;

  ctx.save();

  // Apply clip path if boundary provided
  if (clipBoundary && clipBoundary.length >= 3 && rawWidth && rawHeight) {
    const transformedBoundary = clipBoundary.map((p) =>
      transformPointForOrientation(p.x, p.y, rawWidth, rawHeight, orientation)
    );

    ctx.beginPath();
    ctx.moveTo(transformedBoundary[0].x, transformedBoundary[0].y);
    for (let i = 1; i < transformedBoundary.length; i++) {
      ctx.lineTo(transformedBoundary[i].x, transformedBoundary[i].y);
    }
    ctx.closePath();
    ctx.clip();
  }

  ctx.globalAlpha = MASK_ALPHA;

  // Create offscreen canvas for full mask
  const maskCanvas = document.createElement("canvas");
  maskCanvas.width = displayWidth;
  maskCanvas.height = displayHeight;
  const maskCtx = maskCanvas.getContext("2d");
  if (!maskCtx) {
    ctx.restore();
    return;
  }

  const imageData = maskCtx.createImageData(displayWidth, displayHeight);
  const data = imageData.data;

  for (let y = 0; y < displayHeight; y++) {
    for (let x = 0; x < displayWidth; x++) {
      const maskX = Math.floor(x / scaleX);
      const maskY = Math.floor(y / scaleY);

      if (maskY < maskHeight && maskX < maskWidth && mask[maskY][maskX]) {
        const idx = (y * displayWidth + x) * 4;
        data[idx] = rgb.r;
        data[idx + 1] = rgb.g;
        data[idx + 2] = rgb.b;
        data[idx + 3] = 255;
      }
    }
  }

  maskCtx.putImageData(imageData, 0, 0);
  ctx.drawImage(maskCanvas, 0, 0);
  ctx.restore();
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Convert hex color to RGB object.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}
